/**
 * 💰 DASHBOARD ADMIN AFFILIATION
 * Gestion complète du programme d'affiliation depuis le SupremeAdmin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, DollarSign, TrendingUp, Link2, MousePointer,
  Award, CheckCircle, XCircle, Clock, ArrowUpRight,
  RefreshCw, Eye, Download, Filter, Search, X,
  ChevronRight, Wallet, CreditCard, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant carte stat
const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`rounded-xl p-4 border border-${color}-500/30 bg-gradient-to-br from-${color}-900/20 to-transparent`}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 text-${color}-400`} />
      {trend && (
        <span className={`text-xs flex items-center gap-1 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
          <ArrowUpRight className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-gray-500 text-sm">{label}</p>
    {subValue && <p className="text-xs text-gray-600 mt-1">{subValue}</p>}
  </motion.div>
);

// Composant affilié
const AffiliateRow = ({ affiliate, onView }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:border-pink-500/30 transition-colors"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold">
        {affiliate.first_name?.[0] || 'A'}
      </div>
      <div>
        <p className="text-white font-medium">{affiliate.first_name} {affiliate.last_name}</p>
        <p className="text-pink-400 text-sm font-mono">{affiliate.affiliate_code}</p>
      </div>
    </div>
    <div className="flex items-center gap-6">
      <div className="text-right">
        <p className="text-green-400 font-bold">{affiliate.stats?.total_earned?.toFixed(2) || '0.00'}€</p>
        <p className="text-gray-500 text-xs">Gagné</p>
      </div>
      <div className="text-right">
        <p className="text-white">{affiliate.stats?.total_referrals?.N1 || 0}</p>
        <p className="text-gray-500 text-xs">Filleuls</p>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs ${
        affiliate.status === 'active' ? 'bg-green-500/20 text-green-400' :
        affiliate.status === 'blacklisted' ? 'bg-red-500/20 text-red-400' :
        'bg-yellow-500/20 text-yellow-400'
      }`}>
        {affiliate.status}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onView(affiliate)}
        className="text-gray-400 hover:text-white"
      >
        <Eye className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

// Composant retrait en attente
const WithdrawalRow = ({ withdrawal, onProcess }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center justify-between p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
  >
    <div className="flex items-center gap-3">
      <Wallet className="w-5 h-5 text-yellow-400" />
      <div>
        <p className="text-white font-medium">{withdrawal.amount?.toFixed(2)}€</p>
        <p className="text-gray-500 text-xs">
          via {withdrawal.method === 'paypal' ? 'PayPal' : withdrawal.method === 'iban' ? 'Virement' : 'Crédit'}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        onClick={() => onProcess(withdrawal.id, 'approve')}
        className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
      >
        <CheckCircle className="w-4 h-4 mr-1" />
        Approuver
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onProcess(withdrawal.id, 'reject')}
        className="text-red-400 hover:bg-red-500/20"
      >
        <XCircle className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

export default function AffiliationAdminDashboard({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Charger l'overview
      const overviewRes = await fetch(`${API_URL}/api/affiliation/admin/overview`);
      const overviewData = await overviewRes.json();
      if (overviewData.success) {
        setOverview(overviewData);
      }

      // Charger les affiliés
      const affiliatesRes = await fetch(`${API_URL}/api/affiliation/admin/affiliates?status=${statusFilter}`);
      const affiliatesData = await affiliatesRes.json();
      if (affiliatesData.success) {
        setAffiliates(affiliatesData.affiliates || []);
      }
    } catch (error) {
      console.error('Erreur chargement données affiliation:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/affiliation/admin/process-withdrawal/${withdrawalId}?action=${action}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        loadData();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de traitement');
    }
  };

  const filteredAffiliates = affiliates.filter(a => 
    !searchTerm || 
    a.affiliate_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#0a0505]/95 overflow-y-auto"
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Programme d'Affiliation</h1>
              <p className="text-gray-500">Gestion des affiliés et commissions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={loadData}
              variant="outline"
              className="border-pink-500/50 text-pink-400 hover:bg-pink-500/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {loading && !overview ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-pink-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
              <StatCard
                icon={Users}
                label="Affiliés actifs"
                value={overview?.overview?.totalAffiliates || 0}
                color="pink"
              />
              <StatCard
                icon={Link2}
                label="Filleuls total"
                value={overview?.overview?.totalReferrals || 0}
                color="purple"
              />
              <StatCard
                icon={Award}
                label="Abonnés actifs"
                value={overview?.overview?.activeSubscribers || 0}
                color="green"
              />
              <StatCard
                icon={TrendingUp}
                label="Taux conversion"
                value={`${overview?.overview?.conversionRate || 0}%`}
                color="cyan"
              />
              <StatCard
                icon={MousePointer}
                label="Clics ce mois"
                value={overview?.thisMonth?.clicks || 0}
                subValue={`${overview?.thisMonth?.conversions || 0} conversions`}
                color="orange"
              />
              <StatCard
                icon={DollarSign}
                label="Commissions"
                value={`${(overview?.commissions?.pending || 0).toFixed(2)}€`}
                subValue="En attente"
                color="yellow"
              />
            </div>

            {/* Commissions Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="rounded-xl p-4 border border-yellow-500/30 bg-yellow-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 font-medium">En attente</span>
                </div>
                <p className="text-3xl font-bold text-white">{(overview?.commissions?.pending || 0).toFixed(2)}€</p>
                <p className="text-gray-500 text-sm">Validation dans 30 jours</p>
              </div>
              <div className="rounded-xl p-4 border border-green-500/30 bg-green-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">Validées</span>
                </div>
                <p className="text-3xl font-bold text-white">{(overview?.commissions?.validated || 0).toFixed(2)}€</p>
                <p className="text-gray-500 text-sm">Prêtes à payer</p>
              </div>
              <div className="rounded-xl p-4 border border-blue-500/30 bg-blue-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-medium">Payées</span>
                </div>
                <p className="text-3xl font-bold text-white">{(overview?.commissions?.paid || 0).toFixed(2)}€</p>
                <p className="text-gray-500 text-sm">Total versé</p>
              </div>
            </div>

            {/* Retraits en attente */}
            {overview?.pendingWithdrawals?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-yellow-400" />
                  Retraits en attente ({overview.pendingWithdrawals.length})
                </h2>
                <div className="space-y-2">
                  {overview.pendingWithdrawals.map(w => (
                    <WithdrawalRow
                      key={w.id}
                      withdrawal={w}
                      onProcess={handleProcessWithdrawal}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top Affiliés */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-400" />
                Top 10 Affiliés
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {overview?.topAffiliates?.map((a, idx) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-4 p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-300 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-white/20 text-white'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{a.name}</p>
                      <p className="text-pink-400 text-sm font-mono">{a.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{a.totalEarned?.toFixed(2)}€</p>
                      <p className="text-gray-500 text-xs">{a.referrals} filleuls</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Liste des affiliés */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Tous les affiliés
                </h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher..."
                      className="pl-9 bg-white/5 border-white/10 text-white w-64"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="all">Tous</option>
                    <option value="active">Actifs</option>
                    <option value="suspended">Suspendus</option>
                    <option value="blacklisted">Blacklistés</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredAffiliates.map(affiliate => (
                  <AffiliateRow
                    key={affiliate.id}
                    affiliate={affiliate}
                    onView={setSelectedAffiliate}
                  />
                ))}
                {filteredAffiliates.length === 0 && (
                  <p className="text-gray-500 text-center py-8">Aucun affilié trouvé</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal détail affilié */}
      <AnimatePresence>
        {selectedAffiliate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-60 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setSelectedAffiliate(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a0a2e] rounded-2xl p-6 max-w-lg w-full border border-pink-500/30"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Détails affilié</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedAffiliate(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                    {selectedAffiliate.first_name?.[0]}
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">
                      {selectedAffiliate.first_name} {selectedAffiliate.last_name}
                    </p>
                    <p className="text-pink-400 font-mono">{selectedAffiliate.affiliate_code}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-gray-500 text-sm">Total gagné</p>
                    <p className="text-green-400 font-bold text-lg">
                      {selectedAffiliate.stats?.total_earned?.toFixed(2) || '0.00'}€
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-gray-500 text-sm">Solde disponible</p>
                    <p className="text-white font-bold text-lg">
                      {selectedAffiliate.stats?.available_balance?.toFixed(2) || '0.00'}€
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-gray-500 text-sm">Filleuls N1</p>
                    <p className="text-white font-bold text-lg">
                      {selectedAffiliate.stats?.total_referrals?.N1 || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-gray-500 text-sm">Statut</p>
                    <p className={`font-bold text-lg ${
                      selectedAffiliate.status === 'active' ? 'text-green-400' :
                      selectedAffiliate.status === 'blacklisted' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {selectedAffiliate.status}
                    </p>
                  </div>
                </div>
                
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-gray-500 text-sm mb-1">Inscrit le</p>
                  <p className="text-white">
                    {new Date(selectedAffiliate.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                
                {selectedAffiliate.paypal_email && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-gray-500 text-sm mb-1">PayPal</p>
                    <p className="text-white flex items-center gap-2">
                      {selectedAffiliate.paypal_email}
                      {selectedAffiliate.paypal_email_verified ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Clock className="w-4 h-4 text-yellow-400" />
                      )}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
